import React, { useEffect, useState } from 'react';
import { Card } from '../../components/ui/card';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../../components/ui/tabs';
import { cn } from '../../lib/utils';
import StatsView from './StatsView';
import UsersView from './UsersView';
import JobsView from './JobsView';
import TopicsView from './TopicsView';

interface Props {
  isDark: boolean;
}

export default function SlidesBotPage({ isDark }: Props) {
  const [tab, setTab] = useState<string>('stats');

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1
          className={cn(
            'text-2xl font-bold',
            isDark ? 'text-white' : 'text-[#0F172A]',
          )}
        >
          Slides Bot
        </h1>
        <p
          className={cn(
            'text-sm mt-1',
            isDark ? 'text-slate-400' : 'text-slate-500',
          )}
        >
          @talaba_slides_bot — analytics, users, generations
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="stats">Dashboard</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="jobs">Jobs</TabsTrigger>
          <TabsTrigger value="topics">Topics</TabsTrigger>
        </TabsList>

        <TabsContent value="stats">
          <StatsView isDark={isDark} />
        </TabsContent>
        <TabsContent value="users">
          <UsersView isDark={isDark} />
        </TabsContent>
        <TabsContent value="jobs">
          <JobsView isDark={isDark} />
        </TabsContent>
        <TabsContent value="topics">
          <TopicsView isDark={isDark} />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// Re-export the small Card wrapper used by sub-views.
export function PanelCard({
  children,
  isDark,
  className,
}: {
  children: React.ReactNode;
  isDark: boolean;
  className?: string;
}) {
  return (
    <Card
      className={cn(
        'p-5',
        isDark ? 'bg-slate-900 border-slate-800' : 'bg-white border-slate-200',
        className,
      )}
    >
      {children}
    </Card>
  );
}

export function useFetchOnce<T>(
  fetcher: () => Promise<T>,
  deps: unknown[] = [],
): { data: T | null; loading: boolean; error: string | null; reload: () => void } {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetcher()
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((e) => {
        if (!cancelled) setError((e as Error).message ?? 'Failed to load');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [...deps, tick]);

  return { data, loading, error, reload: () => setTick((t) => t + 1) };
}
